/* eslint-disable max-len */
/**
 * @fileoverview Step: Brief Review
 *               Displays the AI-generated creative brief in an editable textarea.
 *               User can review and tweak the brief before proceeding.
 *               Tone + longevity selection has been moved to Step 1 (Mission Briefing).
 *               PATH: src/components/contentHub/PreGenerationAdjustments.js
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FiLoader, FiArrowLeft, FiArrowRight, FiEdit3 } from 'react-icons/fi';

import { FINE_TUNE_COPY } from '../shared/constants/workflowCopy';

function PreGenerationAdjustments({
    creativeBrief,
    onAdjustmentsConfirm,
    loading,
    onBack,
    workflowConfig,
}) {
    const [editedBrief, setEditedBrief] = useState(creativeBrief || '');
    const [isEditing, setIsEditing] = useState(false);

    const wordCount = editedBrief.trim().split(/\s+/).filter(Boolean).length;
    const isBriefValid = editedBrief.trim().length >= 20;

    const handleConfirm = useCallback(() => {
        if (loading || !isBriefValid) return;

        // Build adjustments from workflowConfig (tone + longevity now live there)
        const adjustments = {
            tone: workflowConfig?.tone || null,
            longevity: workflowConfig?.longevity || null,
        };

        onAdjustmentsConfirm(adjustments, editedBrief.trim());
    }, [loading, isBriefValid, workflowConfig, editedBrief, onAdjustmentsConfirm]);

    return (
        <div className="bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/10 relative overflow-hidden">

            {/* Header */}
            <div className="p-8 border-b border-white/10 bg-white/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center">
                    <button
                        onClick={onBack}
                        className="mr-6 p-3 text-cream/40 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                        title="Back"
                    >
                        <FiArrowLeft className="h-6 w-6" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">{FINE_TUNE_COPY.header}</h2>
                        <p className="text-sm text-cream/60 mt-1 font-medium">{FINE_TUNE_COPY.subheader}</p>
                    </div>
                </div>
            </div>

            <div className="p-8 space-y-6">

                {/* Creative Brief — Editable */}
                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <p className="text-xs font-black text-indigo-400 uppercase tracking-widest">{FINE_TUNE_COPY.briefLabel}</p>
                            <p className="text-[10px] text-indigo-300/60 mt-1">{FINE_TUNE_COPY.briefHelper}</p>
                        </div>
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-1.5 text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-colors px-3 py-1.5 rounded-lg hover:bg-indigo-500/10"
                            >
                                <FiEdit3 className="h-3.5 w-3.5" />
                                Edit
                            </button>
                        )}
                    </div>

                    {isEditing ? (
                        <div className="space-y-3">
                            <textarea
                                value={editedBrief}
                                onChange={(e) => setEditedBrief(e.target.value)}
                                rows={6}
                                className="w-full bg-warm-dark/50 border border-indigo-500/30 text-indigo-100 text-base leading-relaxed rounded-xl px-5 py-4 focus:outline-none focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-400/50 resize-y"
                                placeholder="Describe the creative direction for this content..."
                            />
                            <div className="flex items-center justify-between">
                                <p className={`text-xs font-medium ${wordCount < 20 ? 'text-soft-coral' : wordCount > 200 ? 'text-amber-400' : 'text-cream/40'}`}>
                                    {wordCount} words
                                </p>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-colors"
                                >
                                    Done editing
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="font-medium text-indigo-100 text-lg leading-relaxed">
                            &ldquo;{editedBrief.replace(/\*\*/g, '')}&rdquo;
                        </p>
                    )}
                </div>

                {!isBriefValid && (
                    <p className="text-xs text-soft-coral font-bold px-2">Brief must be at least 20 characters.</p>
                )}
            </div>

            {/* Footer */}
            <div className="p-8 border-t border-white/10 flex justify-end bg-warm-dark/20 backdrop-blur-sm">
                <button
                    onClick={handleConfirm}
                    disabled={loading || !isBriefValid}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 border border-transparent text-sm font-bold rounded-2xl shadow-lg text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue/50 disabled:opacity-50 transition-all transform hover:scale-[1.02] active:scale-95"
                >
                    {loading ? <FiLoader className="mr-2 h-5 w-5 animate-spin" /> : <FiArrowRight className="mr-2 h-5 w-5" />}
                    {loading ? 'Generating...' : FINE_TUNE_COPY.submitButton}
                </button>
            </div>
        </div>
    );
}

PreGenerationAdjustments.propTypes = {
    creativeBrief: PropTypes.string.isRequired,
    onAdjustmentsConfirm: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    onBack: PropTypes.func.isRequired,
    workflowConfig: PropTypes.object,
};

export default PreGenerationAdjustments;
